import { useMemo, useState, useSyncExternalStore } from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { useAuthActions } from '@/security/auth-provider';
import { useAuthStore } from '@/stores/auth.store';
import { useDocumentStore } from '@/stores';
import { useUiStore } from '@/stores/ui.store';
import { cn } from '@/utils/cn';
import { ArrowLeftRight, ClipboardList, CreditCard, Database, FileCog, FolderInput, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import type { UserRole } from '@/models/auth';
import { SettingsNavigation } from './settings-navigation';

interface AppHeaderProps {
  children?: React.ReactNode;
}

interface NavItem {
  group?: 'settings';
  label: string;
  to: string;
  roles?: UserRole[];
  icon: React.ReactNode;
  eyebrow: string;
  subtitle: string;
}

function HomeIcon() {
  return (
    <svg
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.8}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
      />
    </svg>
  );
}

function DocumentsIcon() {
  return (
    <svg
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.8}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
      />
    </svg>
  );
}

function TemplatesIcon() {
  return (
    <svg
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.8}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.5a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25"
      />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.8}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
      />
    </svg>
  );
}

function AuditLogIcon() {
  return (
    <svg
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.8}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z"
      />
    </svg>
  );
}

function EntitiesIcon() {
  return (
    <svg
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.8}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
      />
    </svg>
  );
}

const NAV_ITEMS: NavItem[] = [
  {
    label: 'File Naming',
    group: 'settings',
    to: '/settings/file-naming',
    icon: <FileCog className="h-4 w-4" />,
    roles: ['admin'],
    eyebrow: 'Download filenames',
    subtitle: 'Configure names for original and final downloads.',
  },
  {
    label: 'Dashboard',
    to: '/dashboard',
    icon: <HomeIcon />,
    eyebrow: 'Operations overview',
    subtitle: 'Monitoring, throughput, and queue health.',
  },
  {
    label: 'Documents',
    to: '/documents',
    icon: <DocumentsIcon />,
    eyebrow: 'Document workspace',
    subtitle: 'Review extraction, routing, and export readiness.',
  },
  {
    label: 'Drop Folder Intake',
    to: '/drop-folder',
    icon: <FolderInput className="h-4 w-4" />,
    roles: ['admin'],
    eyebrow: 'SFTP intake',
    subtitle: 'Monitor incoming files and ingestion progress.',
  },
  {
    label: 'Reports',
    to: '/reports',
    icon: <ClipboardList className="h-4 w-4" />,
    eyebrow: 'Operational reports',
    subtitle: 'Review activity, throughput, and compliance trends.',
  },
  {
    label: 'Export',
    to: '/export',
    icon: <ArrowLeftRight className="h-4 w-4" />,
    roles: ['admin'],
    eyebrow: 'Final handoffs',
    subtitle: 'Filter final documents and prepare system import files.',
  },
  {
    label: 'Entities',
    to: '/entities',
    icon: <EntitiesIcon />,
    eyebrow: 'Entity records',
    subtitle: 'Manage entities and their accounts.',
  },
  {
    label: 'SOR Import',
    to: '/sor',
    icon: <Database className="h-4 w-4" />,
    roles: ['admin'],
    eyebrow: 'Reference data',
    subtitle: 'Import this workspace SOR entities and accounts.',
  },
  {
    label: 'Templates',
    group: 'settings',
    to: '/templates',
    icon: <TemplatesIcon />,
    eyebrow: 'Extraction templates',
    subtitle: 'Maintain document schemas and matching rules.',
  },
  {
    label: 'Court Forms Registry',
    group: 'settings',
    to: '/court-forms',
    icon: <FileCog className="h-4 w-4" />,
    roles: ['admin'],
    eyebrow: 'Recognition registry',
    subtitle: 'Maintain court-form recognition signals.',
  },
  {
    label: 'Users',
    group: 'settings',
    to: '/users',
    icon: <UsersIcon />,
    roles: ['admin'],
    eyebrow: 'Workspace access',
    subtitle: 'Manage reviewers, admins, and permissions.',
  },
  {
    label: 'Billing',
    group: 'settings',
    to: '/billing',
    icon: <CreditCard className="h-4 w-4" />,
    roles: ['admin'],
    eyebrow: 'Plan and payments',
    subtitle: 'Review plan, page credits, and invoices.',
  },
  {
    label: 'Audit Log',
    group: 'settings',
    to: '/audit-logs',
    icon: <AuditLogIcon />,
    roles: ['admin'],
    eyebrow: 'Compliance trail',
    subtitle: 'Inspect activity, decisions, and history.',
  },
];

interface UserMenuProps {
  initials: string;
  displayName?: string;
  email?: string;
  activeScopeLabel?: string;
  activeScopeRole?: string;
  switchableScopes: ScopeMenuItem[];
  onScopeSwitch: (scopeKey: string) => void;
  onLogout: () => void;
  compact?: boolean;
}

interface ScopeMenuItem {
  scopeKey: string;
  label: string;
  role: string;
}

interface ProfileScopeMenuContentProps {
  displayName?: string;
  email?: string;
  activeScopeLabel?: string;
  activeScopeRole?: string;
  switchableScopes: ScopeMenuItem[];
  onScopeSwitch: (scopeKey: string) => void;
  onLogout: () => void;
}

const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';

function subscribeToDesktopViewport(onChange: () => void) {
  if (!window.matchMedia) return () => {};
  const query = window.matchMedia(DESKTOP_MEDIA_QUERY);
  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
}

function isDesktopViewport() {
  return !window.matchMedia || window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
}

function ProfileScopeMenuContent({
  displayName,
  email,
  activeScopeLabel,
  activeScopeRole,
  switchableScopes,
  onScopeSwitch,
  onLogout,
}: ProfileScopeMenuContentProps) {
  return (
    <>
      <DropdownMenuLabel className="px-3 py-2.5">
        <div className="text-[13px] font-semibold text-slate-950">{displayName ?? 'User'}</div>
        <div className="mt-0.5 truncate text-[12px] font-normal text-slate-500">{email}</div>
        <div className="mt-3 rounded-xl border border-blue-100 bg-blue-50/80 px-3 py-2">
          <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-blue-700">
            Active workspace
          </div>
          <div className="mt-1 flex items-center justify-between gap-3">
            <span className="min-w-0 truncate text-[13px] font-semibold text-slate-950">
              {activeScopeLabel ?? 'Workspace'}
            </span>
            {activeScopeRole ? (
              <span className="shrink-0 rounded-full bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-blue-700 shadow-sm">
                {activeScopeRole}
              </span>
            ) : null}
          </div>
        </div>
      </DropdownMenuLabel>
      {switchableScopes.length > 0 ? (
        <>
          <DropdownMenuSeparator className="bg-slate-100" />
          <DropdownMenuLabel className="px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
            Available workspaces
          </DropdownMenuLabel>
          {switchableScopes.map((scope) => (
            <DropdownMenuItem
              key={scope.scopeKey}
              onClick={() => onScopeSwitch(scope.scopeKey)}
              className="flex cursor-pointer items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-[13px] font-medium text-slate-700 focus:bg-blue-50 focus:text-slate-950"
            >
              <span className="min-w-0 truncate">{scope.label}</span>
              <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                {scope.role}
              </span>
            </DropdownMenuItem>
          ))}
        </>
      ) : null}
      <DropdownMenuSeparator className="bg-slate-100" />
      <DropdownMenuItem
        onClick={onLogout}
        className="flex cursor-pointer items-center gap-2 rounded-xl px-3 py-2 text-[13px] font-medium text-rose-600 focus:bg-rose-50 focus:text-rose-700"
      >
        <svg
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.8}
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75"
          />
        </svg>
        Log out
      </DropdownMenuItem>
    </>
  );
}

function UserMenu({
  initials,
  displayName,
  email,
  activeScopeLabel,
  activeScopeRole,
  switchableScopes,
  onScopeSwitch,
  onLogout,
  compact = false,
}: UserMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'flex items-center gap-2 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300/70',
            compact
              ? 'p-0 hover:opacity-90'
              : 'rounded-2xl border border-slate-200 bg-white px-2 py-1.5 shadow-sm hover:bg-slate-50',
          )}
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#2d69ef] text-[11px] font-bold text-white shadow-sm">
            {initials}
          </div>
          {!compact && (
            <div className="hidden text-left xl:block">
              <div className="max-w-[140px] truncate text-[12px] font-semibold text-slate-950">
                {displayName ?? 'User'}
              </div>
              <div className="max-w-[140px] truncate text-[11px] text-slate-500">
                {activeScopeLabel ?? 'Workspace'}
              </div>
            </div>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-72 rounded-2xl border-slate-200 bg-white p-1.5 shadow-xl"
      >
        <ProfileScopeMenuContent
          displayName={displayName}
          email={email}
          activeScopeLabel={activeScopeLabel}
          activeScopeRole={activeScopeRole}
          switchableScopes={switchableScopes}
          onScopeSwitch={onScopeSwitch}
          onLogout={onLogout}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function AppHeader({ children }: AppHeaderProps) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const preferredSidebarOpen = useUiStore((state) => state.sidebarOpen);
  const setSidebarOpen = useUiStore((state) => state.setSidebarOpen);
  const { signOut, selectScope } = useAuthActions();
  const navigate = useNavigate();
  const { userInfo, activeScope, availableScopes } = useAuthStore();
  const { setFilter, setPage, setSelectedDocumentId } = useDocumentStore();
  const userRole = userInfo?.role;
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const isDocuments = currentPath === '/documents' || currentPath.startsWith('/documents/');
  const isDesktop = useSyncExternalStore(subscribeToDesktopViewport, isDesktopViewport);
  // Below desktop, Documents keeps the icon rail so the full navigation doesn't squeeze the review panes.
  const sidebarOpen = preferredSidebarOpen && (!isDocuments || isDesktop);

  const visibleNavItems = useMemo(
    () => NAV_ITEMS.filter((item) => !item.roles || (userRole && item.roles.includes(userRole))),
    [userRole],
  );
  const mainNavItems = visibleNavItems.filter((item) => item.group !== 'settings');
  const settingsNavItems = visibleNavItems.filter((item) => item.group === 'settings');

  const handleLogout = () => {
    signOut();
    navigate({ to: '/' });
  };

  const handleDocumentsNavClick = () => {
    setFilter('PendingReview');
    setPage(1);
    setSelectedDocumentId(null);
    setMobileNavOpen(false);
  };

  const switchableScopes = availableScopes.filter(
    (scope) => scope.scopeKey !== activeScope?.scopeKey,
  );

  const handleScopeSwitch = async (scopeKey: string) => {
    await selectScope(scopeKey);
    setPage(1);
    setSelectedDocumentId(null);
    setMobileNavOpen(false);
    navigate({ to: '/dashboard', replace: true });
  };

  const initials = userInfo?.displayName
    ? userInfo.displayName
        .split(' ')
        .map((n: string) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : (userInfo?.email?.[0]?.toUpperCase() ?? '?');

  return (
    <div className={cn('flex min-h-0 min-w-0 flex-1 lg:flex-row', isDocuments ? 'flex-row' : 'flex-col')}>
      {!isDocuments && <header className="flex h-12 shrink-0 items-center justify-between border-b border-[#18305f] bg-[linear-gradient(180deg,#10234c_0%,#122a5a_100%)] px-2.5 shadow-[0_1px_0_rgba(15,23,42,0.18)] lg:hidden">
        <div className="flex min-w-0 items-center gap-2.5">
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetTrigger asChild>
              <button className="inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-lg border border-white/15 bg-white/8 text-white transition-colors hover:bg-white/12 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30">
                <svg
                  className="h-4.5 w-4.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2.2}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                  />
                </svg>
                <span className="sr-only">Open navigation</span>
              </button>
            </SheetTrigger>
            <SheetContent
              side="left"
              className="w-[88vw] max-w-[360px] border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f7faff_100%)] px-0 py-0 sm:max-w-[360px]"
            >
              <SheetHeader className="border-b border-slate-200 px-5 pb-4 pt-6 text-left">
                <SheetTitle className="text-[18px] font-bold tracking-tight text-slate-950">
                  Workspace Menu
                </SheetTitle>
                <SheetDescription className="text-[12px] leading-5 text-slate-500">
                  Jump between sections.
                </SheetDescription>
              </SheetHeader>
              <div className="flex h-full flex-col overflow-y-auto px-4 py-4">
                <nav className="space-y-1.5">
                  {mainNavItems.map((item) => {
                    const isActive =
                      currentPath === item.to || currentPath.startsWith(item.to + '/');
                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        onClick={() => {
                          if (item.to === '/documents') {
                            handleDocumentsNavClick();
                          } else {
                            setMobileNavOpen(false);
                          }
                        }}
                        className={cn(
                          'flex items-center gap-3 rounded-2xl px-4 py-3 text-[13px] font-semibold transition-colors',
                          isActive
                            ? 'bg-blue-600 text-white shadow-[0_12px_24px_-18px_rgba(37,99,235,0.75)]'
                            : 'text-slate-700 hover:bg-slate-100',
                        )}
                      >
                        <span className={cn(isActive ? 'text-white' : 'text-slate-500')}>
                          {item.icon}
                        </span>
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}
                  <SettingsNavigation items={settingsNavItems} currentPath={currentPath} mobile
                    onNavigate={() => setMobileNavOpen(false)} />
                </nav>
              </div>
            </SheetContent>
          </Sheet>

          <Link to="/dashboard" className="min-w-0 no-underline">
            <div className="truncate text-[14px] font-semibold text-white">Classifai</div>
          </Link>
        </div>

        <UserMenu
          initials={initials}
          displayName={userInfo?.displayName}
          email={userInfo?.email}
          activeScopeLabel={activeScope?.label}
          activeScopeRole={activeScope?.role}
          switchableScopes={switchableScopes}
          onScopeSwitch={(scopeKey) => {
            void handleScopeSwitch(scopeKey);
          }}
          onLogout={handleLogout}
          compact
        />
      </header>}

      <aside
        className={cn(
          'shrink-0 flex-col border-r border-[#1c335f] bg-[radial-gradient(circle_at_top,#1b3771_0%,rgba(27,55,113,0)_28%),linear-gradient(180deg,#0d1730_0%,#12264c_58%,#102145_100%)] text-white transition-[width] duration-200 lg:flex',
          isDocuments ? 'flex' : 'hidden lg:flex',
          sidebarOpen ? 'w-[224px] xl:w-[232px]' : isDocuments ? 'w-12 sm:w-16' : 'w-16',
        )}
      >
        <div className={cn('border-b border-white/8 pb-3 pt-4', sidebarOpen ? 'px-3.5' : 'px-2')}>
          <div
            className={cn(
              'flex',
              sidebarOpen ? 'items-center gap-2.5' : 'flex-col items-center gap-2.5',
            )}
          >
            <Link to="/dashboard" className="shrink-0 no-underline">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/12 text-[11px] font-bold text-white shadow-[0_10px_20px_rgba(8,15,35,0.18)] backdrop-blur-sm">
                CL
              </div>
            </Link>
            {sidebarOpen ? (
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-center gap-2">
                  <Link
                    to="/dashboard"
                    className="min-w-0 flex-1 truncate text-[17px] font-bold tracking-tight text-white no-underline"
                  >
                    Classifai
                  </Link>
                  <button
                    type="button"
                    onClick={() => setSidebarOpen(false)}
                    aria-label="Collapse navigation"
                    title="Collapse navigation"
                    className="ml-auto inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/[0.06] text-blue-100/75 transition-colors hover:bg-white/[0.12] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
                  >
                    <PanelLeftClose className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                aria-label="Expand navigation"
                title="Expand navigation"
                className={cn(
                  'inline-flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 bg-white/[0.06] text-blue-100/75 transition-colors hover:bg-white/[0.12] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30',
                  // Small screens keep the icon rail on Documents; the full nav would squeeze the panes.
                  isDocuments && 'hidden lg:inline-flex',
                )}
              >
                <PanelLeftOpen className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {sidebarOpen && (
            <div className="px-2 text-[9px] font-bold uppercase tracking-[0.18em] text-white/70">
              Navigation
            </div>
          )}
          <nav className={cn(sidebarOpen ? 'mt-2 space-y-0.5' : 'space-y-1.5')}>
            {mainNavItems.map((item) => {
              const isActive = currentPath === item.to || currentPath.startsWith(item.to + '/');
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={item.to === '/documents' ? handleDocumentsNavClick : undefined}
                  title={sidebarOpen ? undefined : item.label}
                  aria-label={sidebarOpen ? undefined : item.label}
                  className={cn(
                    'group flex items-center rounded-lg text-[12px] font-semibold transition-all',
                    sidebarOpen ? 'gap-2 px-2.5 py-1.5' : 'h-9 justify-center px-0',
                    isActive
                      ? 'bg-white/12 text-white shadow-[0_12px_24px_-18px_rgba(15,23,42,0.55)] ring-1 ring-white/12'
                      : 'text-blue-100/78 hover:bg-white/6 hover:text-white',
                  )}
                >
                  <span
                    className={cn(
                      'flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors',
                      isActive
                        ? 'bg-white/12 text-white'
                        : 'bg-white/[0.04] text-blue-100/70 group-hover:text-white',
                    )}
                  >
                    {item.icon}
                  </span>
                  {sidebarOpen && <span className="truncate">{item.label}</span>}
                </Link>
              );
            })}
            <SettingsNavigation items={settingsNavItems} currentPath={currentPath} collapsed={!sidebarOpen} />
          </nav>
        </div>

        <div className={cn('border-t border-white/8 py-2.5', sidebarOpen ? 'px-3' : 'px-2')}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={cn(
                  'flex w-full items-center rounded-[18px] border border-white/10 bg-black/10 text-left transition-colors hover:bg-black/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30',
                  sidebarOpen ? 'gap-2.5 px-3 py-2' : 'justify-center px-0 py-2',
                )}
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#2d69ef] text-[10px] font-bold text-white shadow-sm">
                  {initials}
                </div>
                {sidebarOpen && (
                  <>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[13px] font-semibold text-white">
                        {userInfo?.displayName ?? 'User'}
                      </div>
                      <div className="truncate text-[11px] text-white/70">{userInfo?.email}</div>
                    </div>
                    <svg
                      className="h-4 w-4 shrink-0 text-white/70"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
                      />
                    </svg>
                  </>
                )}
                <span className="sr-only">Open user menu</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              side="top"
              className="w-72 rounded-2xl border-slate-200 bg-white p-1.5 shadow-xl"
            >
              <ProfileScopeMenuContent
                displayName={userInfo?.displayName}
                email={userInfo?.email}
                activeScopeLabel={activeScope?.label}
                activeScopeRole={activeScope?.role}
                switchableScopes={switchableScopes}
                onScopeSwitch={(scopeKey) => {
                  void handleScopeSwitch(scopeKey);
                }}
                onLogout={handleLogout}
              />
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* The desktop top bar that previously sat here (page eyebrow + title +
          user menu) was removed. Page titles live in each route's own
          PageIntro; the user/scope menu has moved to the sidebar's bottom
          panel below (mirrors the admin console's layout). */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">{children}</div>
    </div>
  );
}
